import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle, Download, Eye } from "lucide-react";
import toast from "react-hot-toast";
import { authService, employeeService } from "../../services/api";
import { getAuthItem } from "../../utils/authStorage";
import AttachmentPreviewModal from "../../components/Common/AttachmentPreviewModal";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentYear = new Date().getFullYear();

function useEmployeeId() {
  const [employeeId, setEmployeeId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userRes = await authService.getCurrentUser();
        const id = userRes?.data?.employeeId ?? userRes?.employeeId ?? null;
        if (!cancelled) setEmployeeId(id);
      } catch {
        if (!cancelled) setEmployeeId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return employeeId;
}

function usePayslipYearOptions(employeeId) {
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [companyServiceYear, setCompanyServiceYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    if (!employeeId) {
      setEmployeeProfile(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await employeeService.getEmployee(employeeId);
        const emp = res?.data ?? res;
        if (!cancelled) setEmployeeProfile(emp || null);
      } catch {
        if (!cancelled) setEmployeeProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  useEffect(() => {
    let cancelled = false;
    const raw = getAuthItem("companyData");
    if (!raw) {
      setCompanyServiceYear(null);
      return undefined;
    }
    try {
      const company = JSON.parse(raw);
      const serviceOn =
        company?.service_commences_on ?? company?.serviceCommencesOn ?? null;
      const year = serviceOn ? new Date(serviceOn).getFullYear() : null;
      if (!cancelled) setCompanyServiceYear(Number.isNaN(year) ? null : year);
    } catch {
      if (!cancelled) setCompanyServiceYear(null);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const yearOptions = useMemo(() => {
    const doj =
      employeeProfile?.joiningDate ??
      employeeProfile?.dateOfJoin ??
      employeeProfile?.dateOfJoining ??
      employeeProfile?.serviceCommencedDate ??
      null;
    const dojYear = doj ? new Date(doj).getFullYear() : null;
    const serviceYear = companyServiceYear ?? null;
    const minYear = [serviceYear, dojYear].filter((y) => y != null).length
      ? Math.max(...[serviceYear, dojYear].filter((y) => y != null))
      : currentYear;
    const from = Math.min(minYear, currentYear);
    return Array.from({ length: currentYear - from + 1 }, (_, i) => currentYear - i);
  }, [employeeProfile, companyServiceYear]);

  useEffect(() => {
    if (yearOptions.length && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions, selectedYear]);

  return { selectedYear, setSelectedYear, yearOptions };
}

export function EmployeePayslipsPanel() {
  const employeeId = useEmployeeId();
  const { selectedYear, setSelectedYear, yearOptions } = usePayslipYearOptions(employeeId);
  const [payslipsByMonth, setPayslipsByMonth] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: null,
    blobUrlToRevoke: null,
  });

  const fetchMyPayslips = useCallback(async (year) => {
    setLoading(true);
    try {
      const res = await employeeService.getMyPayslipsForYear(year);
      if (res?.success && Array.isArray(res.data)) {
        const map = {};
        res.data.forEach((entry) => {
          map[entry.monthYear] = entry;
        });
        setPayslipsByMonth(map);
      } else {
        setPayslipsByMonth({});
      }
    } catch (e) {
      console.error("Error fetching my payslips:", e);
      toast.error("Failed to load payslips");
      setPayslipsByMonth({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyPayslips(selectedYear);
  }, [selectedYear, fetchMyPayslips]);

  const closePreview = () => {
    if (previewState.blobUrlToRevoke) {
      window.URL.revokeObjectURL(previewState.blobUrlToRevoke);
    }
    setPreviewState({
      isOpen: false,
      url: null,
      downloadUrl: null,
      fileName: null,
      blobUrlToRevoke: null,
    });
  };

  const resolvePayslipType = (entry) =>
    entry?.documents?.find((d) => d.type === "standard")?.type ??
    entry?.documents?.[0]?.type ??
    "standard";

  const fetchPayslipBlob = async (monthYear, entry) => {
    if (!employeeId) {
      toast.error("Unable to identify employee");
      return null;
    }
    const type = resolvePayslipType(entry);
    const data = await employeeService.downloadPayslip(employeeId, monthYear, type);
    const blob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
    return { blob, fileName: `${monthYear}.pdf` };
  };

  const handleViewPayslip = async (monthYear, entry) => {
    const toastId = toast.loading("Generating payslip PDF… please wait");
    try {
      const result = await fetchPayslipBlob(monthYear, entry);
      if (!result) {
        toast.dismiss(toastId);
        return;
      }
      const url = window.URL.createObjectURL(result.blob);
      setPreviewState({
        isOpen: true,
        url,
        downloadUrl: url,
        fileName: result.fileName,
        blobUrlToRevoke: url,
      });
      toast.success("Payslip ready", { id: toastId });
    } catch (e) {
      console.error("View payslip error:", e);
      toast.error(e?.response?.data?.message || "Failed to preview payslip PDF", {
        id: toastId,
      });
    }
  };

  const handleDownloadPayslip = async (monthYear, entry) => {
    const toastId = toast.loading("Preparing payslip download… please wait");
    try {
      const result = await fetchPayslipBlob(monthYear, entry);
      if (!result) {
        toast.dismiss(toastId);
        return;
      }
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Download started", { id: toastId });
    } catch (e) {
      console.error("Download payslip error:", e);
      toast.error(e?.response?.data?.message || "Failed to download payslip", {
        id: toastId,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Payslips</h3>
          <p className="mt-0.5 text-sm text-slate-600">
            Payslips published by payroll appear here by month. Preview opens a PDF viewer; use Download to save the file.
          </p>
        </div>
        <label className="block text-sm text-slate-600">
          Year
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="mt-1 block w-32 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm shadow-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading payslips…</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-amber-50/80 text-left text-slate-700">
                <th className="px-4 py-2.5 font-semibold">Month</th>
                <th className="px-4 py-2.5 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((month) => {
                const monthYear = `${month}-${selectedYear}`;
                const entry = payslipsByMonth[monthYear];
                const hasPayslip = entry?.documents?.length > 0;
                return (
                  <tr key={month} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 text-slate-800">{month}</td>
                    <td className="px-4 py-2.5">
                      {hasPayslip ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewPayslip(monthYear, entry)}
                            className="inline-flex items-center gap-1.5 rounded-lg border-2 border-amber-500/40 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-50"
                            title="Preview payslip PDF"
                          >
                            <Eye size={16} />
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadPayslip(monthYear, entry)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
                            title="Download payslip PDF"
                          >
                            <Download size={16} />
                            Download
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={previewState.isOpen}
        onClose={closePreview}
        url={previewState.url}
        downloadUrl={previewState.downloadUrl}
        fileName={previewState.fileName}
      />
    </div>
  );
}

export function EmployeeForm16Panel() {
  const [form16Data, setForm16Data] = useState([]);

  useEffect(() => {
    fetch("/api/form16")
      .then((res) => res.json())
      .then((data) => setForm16Data(Array.isArray(data) ? data : []))
      .catch(() => setForm16Data([]));
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Form-16</h3>
        <p className="mt-0.5 text-sm text-slate-600">
          Annual TDS certificate (Part A / Part B) when issued by payroll.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {["Part-A", "Part-B"].map((part) => (
          <div
            key={part}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white"
          >
            <div className="border-b border-slate-100 bg-amber-50/80 px-4 py-2.5 text-sm font-semibold text-slate-800">
              {part}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-4 py-2 text-left font-medium">Year</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(form16Data.length ? form16Data : [{ year: "—" }]).map((yearData) => (
                  <tr key={`${part}-${yearData.year}`} className="border-t border-slate-100">
                    <td className="px-4 py-2.5">{yearData.year}</td>
                    <td className="px-4 py-2.5">
                      {yearData[part] ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
